import React, { useState } from "react";
import { Button, Input } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  TagOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import api from "api";

export default function CouponInput({
  bookingId,
  grandTotal,
  onCouponApplied,
  onCouponRemoved,
  disabled = false,
}) {
  const [couponCode, setCouponCode] = useState("");
  const [isApplied, setIsApplied] = useState(false);
  const [discountData, setDiscountData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleApply = async () => {
    if (!couponCode.trim() || loading) return;

    setError("");
    setLoading(true);

    try {
      const { data } = await api.couponApi.validateCoupon({
        booking_id: bookingId,
        coupon_code: couponCode.trim(),
      });

      if (data.code === 200) {
        const result = data.data;
        setDiscountData(result);
        setIsApplied(true);
        setError("");

        if (onCouponApplied) {
          onCouponApplied({
            discount_amount: result.discount_amount,
            discounted_grand_total: result.discounted_grand_total,
            coupon_code: result.coupon_code,
          });
        }
      } else {
        setError(data.message || "ไม่สามารถใช้คูปองได้");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (loading) return;

    setLoading(true);

    try {
      await api.couponApi.removeCoupon({
        booking_id: bookingId,
      });

      setCouponCode("");
      setIsApplied(false);
      setDiscountData(null);
      setError("");

      if (onCouponRemoved) {
        onCouponRemoved();
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCouponCode(value);
    if (error) setError("");
  };

  if (isApplied && discountData) {
    return (
      <div style={styles.container}>
        <div style={styles.appliedContainer}>
          <div style={styles.appliedHeader}>
            <CheckCircleFilled style={styles.successIcon} />
            <span style={styles.appliedLabel}>คูปองที่ใช้</span>
          </div>
          <div style={styles.appliedContent}>
            <div style={styles.appliedCodeRow}>
              <TagOutlined style={styles.tagIcon} />
              <span style={styles.appliedCode}>
                {discountData.coupon_code}
              </span>
            </div>
            <div style={styles.discountInfo}>
              <div style={styles.discountRow}>
                <span style={styles.discountLabel}>ส่วนลด:</span>
                <span style={styles.discountAmount}>
                  -{new Intl.NumberFormat().format(discountData.discount_amount)} บาท
                </span>
              </div>
              <div style={styles.discountRow}>
                <span style={styles.discountLabel}>ยอดรวมใหม่:</span>
                <span style={styles.newTotal}>
                  {new Intl.NumberFormat().format(discountData.discounted_grand_total)} บาท
                </span>
              </div>
            </div>
          </div>
          <Button
            type="text"
            danger
            icon={loading ? <LoadingOutlined /> : <DeleteOutlined />}
            onClick={handleRemove}
            disabled={disabled || loading}
            style={styles.removeButton}
            aria-label="ลบคูปอง"
          >
            ลบคูปอง
          </Button>
        </div>
        {error && <div style={styles.errorText}>{error}</div>}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.inputRow}>
        <Input
          value={couponCode}
          onChange={handleInputChange}
          placeholder="Enter coupon code"
          maxLength={50}
          disabled={disabled || loading}
          style={styles.input}
          prefix={<TagOutlined style={styles.inputPrefix} />}
          onPressEnter={handleApply}
          aria-label="กรอกรหัสคูปอง"
          aria-describedby={error ? "coupon-error" : undefined}
          aria-invalid={!!error}
        />
        <Button
          type="primary"
          onClick={handleApply}
          disabled={!couponCode.trim() || disabled || loading}
          loading={loading}
          style={styles.applyButton}
          aria-label="ใช้คูปอง"
        >
          ใช้คูปอง
        </Button>
      </div>
      {error && (
        <div
          id="coupon-error"
          style={styles.errorContainer}
          role="alert"
          aria-live="polite"
        >
          <CloseCircleFilled style={styles.errorIcon} />
          <span style={styles.errorText}>{error}</span>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "12px 0",
  },
  inputRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  input: {
    flex: 1,
    height: "38px",
    borderRadius: "8px",
  },
  inputPrefix: {
    color: "#999",
  },
  applyButton: {
    height: "38px",
    borderRadius: "8px",
    fontWeight: "500",
  },
  errorContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 10px",
    borderRadius: "6px",
    backgroundColor: "#fff2f0",
  },
  errorIcon: {
    color: "#ff4d4f",
    fontSize: "14px",
  },
  errorText: {
    color: "#ff4d4f",
    fontSize: "13px",
  },
  appliedContainer: {
    background: "#f6ffed",
    border: "1px solid #b7eb8f",
    borderRadius: "10px",
    padding: "12px 14px",
  },
  appliedHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "8px",
  },
  successIcon: {
    color: "#52c41a",
    fontSize: "16px",
  },
  appliedLabel: {
    fontWeight: "600",
    fontSize: "14px",
    color: "#333",
  },
  appliedContent: {
    marginBottom: "10px",
  },
  appliedCodeRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "8px",
  },
  tagIcon: {
    color: "#52c41a",
    fontSize: "14px",
  },
  appliedCode: {
    fontWeight: "700",
    fontSize: "15px",
    color: "#389e0d",
    letterSpacing: "0.5px",
  },
  discountInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  discountRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  discountLabel: {
    fontSize: "13px",
    color: "#666",
  },
  discountAmount: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#ff4d4f",
  },
  newTotal: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#389e0d",
  },
  removeButton: {
    marginTop: "4px",
    padding: "4px 8px",
    height: "auto",
    fontSize: "13px",
  },
};
